import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Badge } from '@/common/ui/badge'
import Spinner from '@/common/components/Spinner'
import { Button } from '@/common/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Separator } from '@/common/ui/separator'
import {
	CheckCircle2,
	Clock,
	XCircle,
	AlertCircle,
	Upload,
	Loader2,
	CheckCircle,
	Copy,
} from 'lucide-react'
import { toast } from 'sonner'
import useMutationUploadOperatorDepositSlip from '@/modules/bank/api/hooks/useMutationUploadOperatorDepositSlip'
import {
	getAdminSlipPresignUrl,
	getOperatorDepositTransactionById,
} from '@/modules/bank/api/services'
import { BankQueryKeys } from '@/modules/bank/keys'
import { formatBankName } from '@/modules/bank/utils/formatBankName'
import type { OperatorDepositTransactionById } from '@/modules/bank/type'

interface DepositStatusModalProps {
	transactionId: string | null
	open: boolean
	onOpenChange: (open: boolean) => void
	enableUploadSlip?: boolean
}

type StatusKey =
	| 'PENDING'
	| 'PROCESSING'
	| 'WAITING'
	| 'COMPLETED'
	| 'SUCCESS'
	| 'CANCELLED'
	| 'FAILED'
	| 'CREATED'

const STATUS_CONFIG: Record<
	StatusKey,
	{
		variant: 'secondary' | 'default' | 'destructive'
		label: string
		icon: typeof Clock
		color: string
	}
> = {
	PENDING: {
		variant: 'secondary',
		label: 'รอดำเนินการ',
		icon: Clock,
		color: 'text-yellow-600',
	},
	PROCESSING: {
		variant: 'secondary',
		label: 'กำลังตรวจสอบ',
		icon: Clock,
		color: 'text-amber-600',
	},
	WAITING: {
		variant: 'secondary',
		label: 'รอการตรวจสอบ',
		icon: AlertCircle,
		color: 'text-orange-600',
	},
	COMPLETED: {
		variant: 'default',
		label: 'สำเร็จ',
		icon: CheckCircle2,
		color: 'text-green-600',
	},
	SUCCESS: {
		variant: 'default',
		label: 'สำเร็จ',
		icon: CheckCircle2,
		color: 'text-green-600',
	},
	CANCELLED: {
		variant: 'destructive',
		label: 'ยกเลิก',
		icon: XCircle,
		color: 'text-red-600',
	},
	FAILED: {
		variant: 'destructive',
		label: 'ล้มเหลว',
		icon: XCircle,
		color: 'text-red-600',
	},
	CREATED: {
		variant: 'secondary',
		label: 'สร้างแล้ว',
		icon: Clock,
		color: 'text-blue-600',
	},
}

function StatusBadge({ status }: { status: string }) {
	const config = STATUS_CONFIG[status as StatusKey] ?? STATUS_CONFIG.PENDING
	const Icon = config.icon
	return (
		<div className="flex items-center gap-2">
			<Icon className={`h-5 w-5 ${config.color}`} />
			<Badge variant={config.variant}>{config.label}</Badge>
		</div>
	)
}

const formatDate = (dateString: string | undefined) => {
	if (!dateString) return '-'
	try {
		return format(new Date(dateString), 'dd MMM yyyy HH:mm:ss', { locale: th })
	} catch {
		return '-'
	}
}

const formatAmount = (amount: number) =>
	new Intl.NumberFormat('th-TH', {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount)

function CopyableField({
	label,
	value,
	mono,
}: {
	label: string
	value: string
	mono?: boolean
}) {
	return (
		<div>
			<p className="text-xs text-muted-foreground">{label}</p>
			<div className="flex items-center gap-1.5">
				<p
					className={`text-sm font-semibold ${mono ? 'font-mono tracking-wider' : ''}`}
				>
					{value}
				</p>
				<Button
					variant="ghost"
					size="icon"
					className="h-6 w-6 shrink-0"
					onClick={() => {
						navigator.clipboard.writeText(value)
						toast.success('คัดลอกแล้ว')
					}}
				>
					<Copy className="h-3.5 w-3.5 text-muted-foreground" />
				</Button>
			</div>
		</div>
	)
}

// ── Transfer details card ──────────────────────────────────────────────────
function TransferCard({ data }: { data: OperatorDepositTransactionById }) {
	const hasTransfer =
		data.accountName || data.accountNumber || data.bankProvider
	if (!hasTransfer) return null

	return (
		<Card className="border-blue-200 dark:border-blue-800">
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-semibold text-blue-700 dark:text-blue-400">
					บัญชีรับโอนเงิน
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2">
				{data.bankProvider && (
					<div>
						<p className="text-xs text-muted-foreground">ธนาคาร</p>
						<p className="text-sm font-semibold">
							{formatBankName(data.bankProvider)}
						</p>
					</div>
				)}
				{data.accountName && (
					<CopyableField label="ชื่อบัญชี" value={data.accountName} />
				)}
				{data.accountNumber && (
					<CopyableField label="เลขบัญชี" value={data.accountNumber} mono />
				)}
			</CardContent>
		</Card>
	)
}

// ── QR code card ───────────────────────────────────────────────────────────
function QRCard({ qrCodeUrl }: { qrCodeUrl: string }) {
	return (
		<Card className="border-green-200 dark:border-green-800">
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-semibold text-green-700 dark:text-green-400">
					QR Code สำหรับชำระเงิน
				</CardTitle>
			</CardHeader>
			<CardContent className="flex justify-center">
				<img
					src={qrCodeUrl}
					alt="QR Code"
					className="h-48 w-48 object-contain sm:h-56 sm:w-56"
				/>
			</CardContent>
		</Card>
	)
}

// ── Slip upload ────────────────────────────────────────────────────────────
function SlipUploadSection({ transactionId }: { transactionId: string }) {
	const [uploaded, setUploaded] = useState(false)
	const [isUploading, setIsUploading] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const { mutate: uploadSlip, isPending } =
		useMutationUploadOperatorDepositSlip({
			onSuccess: (data) => {
				if (data.success) {
					setUploaded(true)
					toast.success('อัปโหลดสลิปสำเร็จ', {
						description: 'สลิปได้รับการยืนยันแล้ว โปรดรอสักครู่',
					})
				} else {
					toast.error('ไม่พบรายการฝาก', {
						description: data.message || 'กรุณาลองใหม่อีกครั้ง',
					})
				}
			},
			onError: () => {
				toast.error('เกิดข้อผิดพลาด', {
					description: 'ไม่สามารถอัปโหลดสลิปได้ กรุณาลองใหม่',
				})
			},
		})

	const isBusy = isUploading || isPending

	const handleFileChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0]
			if (!file) return
			e.target.value = ''
			setIsUploading(true)
			try {
				const ext = file.type.split('/')[1]?.replace('jpeg', 'jpg') || 'jpg'
				const { presignedUrl, fileUrl } = await getAdminSlipPresignUrl(ext)
				const res = await fetch(presignedUrl, {
					method: 'PUT',
					body: file,
					headers: { 'Content-Type': file.type },
				})
				if (!res.ok) throw new Error(`R2 upload failed: ${res.status}`)
				uploadSlip({ id: transactionId, imageUrl: fileUrl })
			} catch {
				toast.error('เกิดข้อผิดพลาด', {
					description: 'ไม่สามารถอัปโหลดรูปภาพได้ กรุณาลองใหม่',
				})
			} finally {
				setIsUploading(false)
			}
		},
		[transactionId, uploadSlip],
	)

	if (uploaded) {
		return (
			<div className="flex flex-col items-center gap-1 rounded-lg border border-green-500/20 bg-green-500/10 p-3 text-green-600 dark:text-green-400">
				<CheckCircle className="h-5 w-5" />
				<span className="text-sm font-medium">อัปโหลดสลิปสำเร็จ</span>
				<span className="text-xs text-muted-foreground">โปรดรอการตรวจสอบ</span>
			</div>
		)
	}

	return (
		<div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
			<p className="mb-1 text-center text-sm font-medium">
				อัปโหลดสลิปการโอนเงิน
			</p>
			<p className="mb-3 text-center text-xs text-muted-foreground">
				อัปโหลดสลิปเพื่อส่งให้ระบบตรวจสอบ
			</p>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				className="hidden"
				onChange={handleFileChange}
				disabled={isBusy}
			/>
			<Button
				variant="outline"
				className="w-full border-primary/40 text-primary hover:bg-primary/10"
				onClick={() => fileInputRef.current?.click()}
				disabled={isBusy}
			>
				{isBusy ? (
					<>
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						กำลังอัปโหลด...
					</>
				) : (
					<>
						<Upload className="mr-2 h-4 w-4" />
						อัปโหลดสลิป
					</>
				)}
			</Button>
		</div>
	)
}

// ── Main modal ─────────────────────────────────────────────────────────────
export default function DepositStatusModal({
	transactionId,
	open,
	onOpenChange,
	enableUploadSlip,
}: DepositStatusModalProps) {
	const { data, isLoading, refetch } = useQuery({
		queryKey: [BankQueryKeys.DepositTransactionById, transactionId],
		queryFn: () => getOperatorDepositTransactionById(transactionId!),
		enabled: !!transactionId && open,
		refetchInterval: (query) => {
			const d = query.state.data
			return d?.status === 'PENDING' ||
				d?.status === 'CREATED' ||
				d?.status === 'PROCESSING'
				? 10000
				: false
		},
		refetchIntervalInBackground: false,
	})

	useEffect(() => {
		if (open && transactionId) refetch()
	}, [open, transactionId, refetch])

	const isActive =
		data?.status === 'PENDING' ||
		data?.status === 'CREATED' ||
		data?.status === 'PROCESSING'
	const canUploadSlip =
		enableUploadSlip &&
		!!transactionId &&
		(data?.status === 'CREATED' || data?.status === 'PENDING')

	const hasTransfer = !!(
		data?.accountName ||
		data?.accountNumber ||
		data?.bankProvider
	)
	const hasQr = !!data?.qrCodeUrl

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[90vh] w-[95vw] max-w-2xl flex-col gap-0 p-0">
				<DialogHeader className="shrink-0 px-6 pb-4 pt-6">
					<DialogTitle>สถานะรายการฝากเงิน</DialogTitle>
				</DialogHeader>

				{isLoading && !data ? (
					<div className="flex justify-center px-6 py-8">
						<Spinner />
					</div>
				) : data ? (
					<div className="space-y-4 overflow-y-auto px-6 pb-6">
						{/* ① Payment info — TOP PRIORITY */}
						{(hasQr || hasTransfer) && (
							<div className="space-y-3">
								{hasQr && <QRCard qrCodeUrl={data.qrCodeUrl!} />}
								{hasTransfer && <TransferCard data={data} />}
							</div>
						)}

						{/* ② Slip upload — only when active + enabled */}
						{canUploadSlip && (
							<SlipUploadSection transactionId={transactionId!} />
						)}

						{/* ③ Status */}
						<Card>
							<CardHeader className="pb-3">
								<CardTitle className="text-base">สถานะการทำรายการ</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="flex items-center justify-between">
									<StatusBadge status={data.status} />
									{isActive && (
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<Clock className="h-4 w-4 animate-spin" />
											<span>กำลังตรวจสอบ...</span>
										</div>
									)}
								</div>
							</CardContent>
						</Card>

						{/* ④ Transaction details */}
						<Card>
							<CardHeader className="pb-3">
								<CardTitle className="text-base">
									รายละเอียดการทำรายการ
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div>
										<p className="text-sm text-muted-foreground">รหัสรายการ</p>
										<p className="font-mono text-sm">
											{String(data.txId || data.id)}
										</p>
									</div>
									<div>
										<p className="text-sm text-muted-foreground">จำนวนเงิน</p>
										<p className="text-lg font-semibold text-green-600">
											฿{formatAmount(data.amount)}
										</p>
									</div>
								</div>

								<Separator />

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div>
										<p className="text-sm text-muted-foreground">
											วิธีการชำระเงิน
										</p>
										<p className="text-sm font-medium">{data.method}</p>
									</div>
									{data.bankValidateMethod && (
										<div>
											<p className="text-sm text-muted-foreground">
												วิธีตรวจสอบ
											</p>
											<p className="text-sm font-medium">
												{data.bankValidateMethod}
											</p>
										</div>
									)}
								</div>

								<Separator />

								<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
									<div>
										<p className="text-sm text-muted-foreground">สร้างเมื่อ</p>
										<p className="text-sm">{formatDate(data.createdAt)}</p>
									</div>
									<div>
										<p className="text-sm text-muted-foreground">
											อัปเดตล่าสุด
										</p>
										<p className="text-sm">{formatDate(data.updatedAt)}</p>
									</div>
								</div>

								{data.expiredAt && (
									<div>
										<p className="text-sm text-muted-foreground">
											หมดอายุเมื่อ
										</p>
										<p className="text-sm">{formatDate(data.expiredAt)}</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* ⑤ Action buttons */}
						<div className="flex flex-col-reverse justify-end gap-2 sm:flex-row">
							<Button
								variant="outline"
								onClick={() => onOpenChange(false)}
								disabled={isActive}
								className="w-full sm:w-auto"
							>
								{isActive ? 'รอการตรวจสอบ...' : 'ปิด'}
							</Button>
							{isActive && (
								<Button
									onClick={() => refetch()}
									variant="default"
									className="w-full sm:w-auto"
								>
									รีเฟรช
								</Button>
							)}
						</div>
					</div>
				) : (
					<div className="px-6 py-8 text-center text-muted-foreground">
						ไม่พบข้อมูลรายการ
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
